import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from "../lib/supabase";
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';

const MIN_PASSWORD_LENGTH = 8;

const ConfirmRegistrationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Stato della sessione invito: 'checking' | 'valid' | 'invalid'
  const [sessionState, setSessionState] = useState('checking');

  // ── Verifica che la sessione dell'invito sia ancora valida ────────────────
  // Supabase consuma il token magic-link UNA SOLA VOLTA, in automatico, al
  // caricamento della pagina. Se il link è già stato usato, è scaduto, o la
  // pagina viene ricaricata dopo il consumo del token, la sessione non esiste
  // più e updateUser() fallirebbe con un errore tecnico poco chiaro.
  // Verifichiamo la sessione esplicitamente PRIMA di mostrare il form.
  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!isMounted) return;
      setSessionState(!error && data?.session ? 'valid' : 'invalid');
    };

    checkSession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (session) setSessionState('valid');
      else if (event === 'SIGNED_OUT') setSessionState('invalid');
    });

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  // ── Indicatori live (feedback immediato, prima del submit) ────────────────
  const isPasswordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleFinalizeRegistration = async (e) => {
    e.preventDefault();
    setStatus({ type: '', msg: '' });

    // Validazione lunghezza minima
    if (password.length < MIN_PASSWORD_LENGTH) {
      return setStatus({ type: 'error', msg: t('invite_setup.error_too_short', { min: MIN_PASSWORD_LENGTH }) });
    }

    // Verifica corrispondenza password
    if (password !== confirmPassword) {
      return setStatus({ type: 'error', msg: t('invite_setup.error_match') });
    }

    // Guard: non permettiamo il submit se la sessione dell'invito non è più valida
    const { data: sessionCheck } = await supabase.auth.getSession();
    if (!sessionCheck?.session) {
      setSessionState('invalid');
      return setStatus({ type: 'error', msg: t('invite_setup.error_session_expired') });
    }

    setIsLoading(true);
    try {
      const { data, error: authErr } = await supabase.auth.updateUser({ password: password });
      if (authErr) throw authErr;

      if (data?.user?.id) {
        const { error: profErr } = await supabase
          .from('profili')
          .update({ attivo: true })
          .eq('id', data.user.id);

        if (profErr) console.error("Errore Profilo:", profErr.message);
      }

      setStatus({ type: 'success', msg: t('invite_setup.success') });
      setTimeout(() => navigate('/login-pwa'), 2000);
    } catch (err) {
      // Se l'errore riguarda la sessione, mostriamo un messaggio chiaro invece
      // del messaggio tecnico grezzo di Supabase (es. "Auth session missing!")
      const isSessionError = /session|token|jwt/i.test(err.message || '');
      if (isSessionError) {
        setSessionState('invalid');
        setStatus({ type: 'error', msg: t('invite_setup.error_session_expired') });
      } else {
        setStatus({ type: 'error', msg: err.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Schermata: link invito non più valido ─────────────────────────────────
  if (sessionState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md bg-card rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-border">
          <div className="p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-red-500 mb-5">
              <Icon name="AlertTriangle" size={28} />
            </div>
            <h2 className="text-2xl font-black uppercase tracking-tighter text-red-500 mb-3">
              {t('invite_setup.error_session_title')}
            </h2>
            <p className="text-muted-foreground text-[11px] font-bold uppercase tracking-widest leading-relaxed mb-6">
              {t('invite_setup.error_session_expired')}
            </p>
            <Button
              fullWidth
              onClick={() => navigate('/login-pwa')}
              className="rounded-2xl py-6 font-black uppercase tracking-[0.2em] text-[11px]"
            >
              {t('invite_setup.back_to_login')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Schermata di caricamento durante la verifica iniziale della sessione ──
  if (sessionState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md bg-card rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-border">
        <div className="p-10">
          
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-black uppercase tracking-tighter text-primary">
              {t('invite_setup.title')}
            </h2>
            <p className="text-muted-foreground text-[10px] font-bold uppercase tracking-[0.2em] mt-2">
              {t('invite_setup.desc')}
            </p>
          </div>

          {status.msg && (
            <div className={`mb-6 p-4 rounded-2xl border ${
              status.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-red-50 border-red-100 text-red-500'
            }`}>
              <p className="text-[10px] text-center font-black uppercase tracking-widest leading-relaxed">
                {status.msg}
              </p>
            </div>
          )}

          <form onSubmit={handleFinalizeRegistration} className="space-y-5" autoComplete="off">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="new-password"
                label={t('invite_setup.new_password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={MIN_PASSWORD_LENGTH}
                placeholder="••••••••"
                className="rounded-2xl h-12"
                error={isPasswordTooShort ? t('invite_setup.error_too_short', { min: MIN_PASSWORD_LENGTH }) : undefined}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                autoComplete="new-password"
                label={t('invite_setup.confirm_password')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={MIN_PASSWORD_LENGTH}
                placeholder="••••••••"
                className="rounded-2xl h-12"
                error={passwordsMismatch ? t('invite_setup.error_match') : undefined}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirmPassword(p => !p)}
                className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>

              {/* Indicatore live di corrispondenza — feedback immediato prima del submit */}
              {passwordsMatch && (
                <div className="flex items-center gap-1.5 mt-2 px-1 animate-in fade-in duration-200">
                  <Icon name="CheckCircle2" size={13} className="text-emerald-500" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600">
                    {t('invite_setup.passwords_match')}
                  </span>
                </div>
              )}
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                fullWidth 
                loading={isLoading}
                disabled={isLoading || !password || !confirmPassword}
                className="rounded-2xl py-6 font-black uppercase tracking-[0.2em] text-[11px] shadow-lg shadow-primary/20 transition-all hover:-translate-y-1"
              >
                {t('invite_setup.button')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConfirmRegistrationPage;